import {
    Button,
    InputAdornment,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
} from '@mui/material'
import { DateTime } from 'luxon'
import { isSelectingText } from '@utils'
import { logout } from '@utils/sessions'
import { LoadingButton } from '@mui/lab'
import { useEffect, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DashboardErrorView from '../components/DashboardErrorView'
import DashboardLayout from '../components/DashboardLayout'
import DashboardLoadingDataView from '../components/DashboardLoadingDataView'
import SearchIcon from '@mui/icons-material/Search'
import SelectedRowDetailsDialog from '@components/SelectedRowDetailsDialog'
import TableCellSubText from '@components/TableCellSubText'
import TableHeadCell from '@components/TableHeadCell'
import usePagination from '@hooks/usePagination'
import API from '@enums/api'

export type NTEInvoiceRow = {
    invoice_number: string // i.e, "INV-001234"
    invoice_sequence: number // i.e, 1234
    xero_invoice_id: string
    school_name: string
    school_id: string | number
    created: string
    period_start: string
    period_end: string
    invoice_pdf: string
}

const NTEInvoiceSearchPage = () => {
    const [view, setView] = useState<'search' | 'results'>('search')
    const [error, setError] = useState<string | null>(null)
    const [schoolSearchValue, setSchoolSearchValue] = useState<null | string>(
        null
    )
    const [invoiceSearchValue, setInvoiceSearchValue] = useState<string | null>(
        null
    )
    const [activeTab, setActiveTab] = useState(0)
    const [selectedRow, setSelectedRow] = useState<NTEInvoiceRow | null>(null)
    const [loading, setLoading] = useState(true)
    const [rows, setRows] = useState<NTEInvoiceRow[]>([])
    const [searchedValue, setSearchedValue] = useState('')
    const {
        handlePageChange,
        handleRowsPerPageChange,
        page,
        paginate,
        rowsPerPage,
    } = usePagination()

    /**
     * Page initialisation
     */
    useEffect(() => {
        setLoading(true)

        // Set search values based on URL query parameters if available
        // Useful for when searches are sent between colleagues for example
        const searchParams = new URLSearchParams(window.location.search)
        const type = searchParams.get('type')
        const value = searchParams.get('value')

        if (type === 'invoice') {
            console.log('invoice')
            setInvoiceSearchValue(value)
            setActiveTab(0)
        } else if (type === 'school') {
            console.log('school')
            setSchoolSearchValue(value)
            setActiveTab(1)
        }

        setLoading(false)
    }, [])

    async function handleSearch() {
        // set search value based on the active tab
        const value = activeTab === 0 ? invoiceSearchValue : schoolSearchValue
        const type = activeTab === 0 ? 'invoice' : 'school'

        if (!value) {
            return
        }

        // update the search params in the URL
        const searchParams = new URLSearchParams()
        searchParams.set('type', type)
        searchParams.set('value', value)
        history.pushState(
            null,
            '',
            window.location.pathname + '?' + searchParams.toString()
        )

        // set the searchedValue for presentation
        setSearchedValue(type === 'invoice' ? `INV-${value}` : value)

        // switch to results view
        setView('results')

        // search for results
        setLoading(true)
        const res = await API.searches.nteInvoice(type, value)
        setLoading(false)
        if (!res.ok) {
            if (res.status === 401) {
                logout()
                return
            }
            console.error(res)
            setError('Failed to search')
        } else {
            setRows(res.value)
        }
    }

    if (loading) {
        return <DashboardLoadingDataView loading={loading} />
    }

    if (error) {
        return <DashboardErrorView error={error} loading={loading} />
    }

    if (view === 'search') {
        return (
            <DashboardLayout>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        paddingTop: 64,
                    }}
                >
                    <Paper
                        style={{
                            paddingInline: 32,
                            paddingBlock: 32,
                            textAlign: 'center',
                        }}
                    >
                        <Tabs
                            value={activeTab}
                            onChange={(_, tabIndex) => setActiveTab(tabIndex)}
                            sx={{ marginBottom: '32px' }}
                        >
                            <Tab label="Search by Invoice Number" />
                            <Tab label="Search by School Name" />
                        </Tabs>
                        <div>
                            <TextField
                                sx={{
                                    display:
                                        activeTab !== 0 ? 'none' : undefined,
                                }}
                                id="invoice-number"
                                label="Invoice number"
                                placeholder="002429"
                                value={invoiceSearchValue}
                                onChange={(e) =>
                                    setInvoiceSearchValue(e.target.value)
                                }
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <strong>INV-</strong>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                hidden={activeTab !== 1}
                                id="school-name"
                                label="School name"
                                value={schoolSearchValue}
                                placeholder="AB Tech Community College"
                                onChange={(e) =>
                                    setSchoolSearchValue(e.target.value)
                                }
                                sx={{
                                    width: '100%',
                                    display:
                                        activeTab !== 1 ? 'none' : undefined,
                                }}
                            />
                        </div>
                        <LoadingButton
                            sx={{ marginTop: '32px' }}
                            startIcon={<SearchIcon />}
                            size="small"
                            variant="contained"
                            onClick={handleSearch}
                        >
                            Search
                        </LoadingButton>
                    </Paper>
                </div>
            </DashboardLayout>
        )
    }

    const rowCount = rows.length
    const filteredRows = paginate(rows, page, rowsPerPage)

    // view === "results"
    return (
        <DashboardLayout loading={loading}>
            <SelectedRowDetailsDialog
                onClose={() => setSelectedRow(null)}
                selectedRow={selectedRow}
            />
            <div
                style={{
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Button
                    startIcon={<ArrowBackIcon />}
                    size="small"
                    variant="text"
                    onClick={() => setView('search')}
                >
                    Back to Search
                </Button>
                <div>
                    <p
                        style={{
                            color: 'var(--grey-700)',
                            fontSize: '0.875rem',
                            textAlign: 'right',
                        }}
                    >
                        <strong>
                            Found {rowCount} results for "{searchedValue}"
                        </strong>
                    </p>
                </div>
            </div>
            <TableContainer
                component={Paper}
                sx={{
                    maxWidth: 'calc(100vw - 240px - 32px)',
                    width: '100%',
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableHeadCell>Invoice Number</TableHeadCell>
                            <TableHeadCell>School Name</TableHeadCell>
                            <TableHeadCell>
                                Created
                                <br />
                                <TableCellSubText>
                                    Covering Period
                                </TableCellSubText>
                            </TableHeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    No results found
                                    <br />
                                    <TableCellSubText>
                                        Try changing your search or filters
                                    </TableCellSubText>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredRows.map((row) => {
                                const created = DateTime.fromISO(
                                    row.created
                                ).toLocaleString(DateTime.DATE_MED)
                                const period_start = DateTime.fromISO(
                                    row.period_start
                                ).toLocaleString(DateTime.DATE_SHORT)
                                const period_end = DateTime.fromISO(
                                    row.period_end
                                ).toLocaleString(DateTime.DATE_SHORT)

                                return (
                                    <TableRow
                                        key={row.invoice_number}
                                        onClick={(event) => {
                                            if (isSelectingText()) {
                                                // do nothing
                                            } else if (
                                                event.target instanceof
                                                HTMLAnchorElement
                                            ) {
                                                // do nothing
                                            } else {
                                                setSelectedRow(row)
                                            }
                                        }}
                                        hover
                                    >
                                        <TableCell>
                                            {row.invoice_number}
                                        </TableCell>
                                        <TableCell>{row.school_name}</TableCell>
                                        <TableCell>
                                            {created}
                                            <br />
                                            <TableCellSubText>
                                                {period_start} – {period_end}
                                            </TableCellSubText>
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={rowCount}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                page={page}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50, { value: -1, label: 'All' }]}
            />
        </DashboardLayout>
    )
}

export default NTEInvoiceSearchPage
